import Base from '../Base';
import Component from 'vue-class-component';
import { Button, TextInput, Loading, Spinner, Checkbox } from 'streamlabs-beaker';
import SubscriptionApi from '@/apis/subscription-api';
import { capitalize } from 'lodash';
import NotificationHelper from '@/helpers/notification';
import { TSubscriptionPlanType, ESubscriptionPlanType } from '@/helpers/subscription';
import { EModalName } from '@/helpers/modal';
import { Prop, Watch } from 'vue-property-decorator';
import moment from 'moment';
import CheckoutPlans from './CheckoutPlans.vue';
import PaymentForm from './PaymentForm.vue';
import UtilHelper from '@/helpers/utils';
import { EUserAdEvent } from '@/helpers/user';
import DisclaimerCheckbox from '@/components/checkout/DisclaimerCheckbox.vue';
import { EAnalyticsEvent } from '@/helpers/analytic';

@Component({
  components: {
    SButton: Button,
    STextInput: TextInput,
    SLoading: Loading,
    SSpinner: Spinner,
    SCheckbox: Checkbox,
    CheckoutPlans,
    PaymentForm,
    DisclaimerCheckbox,
  },
})
export default class CheckoutMixin extends Base {
  @Prop({ default: false })
  fromModal: boolean;

  initialLoadCompleted: boolean = false;

  $refs!: {
    paymentForm: PaymentForm;
  };

  async mounted() {
    if (!this.plans) {
      await this.loadPlans(this.abGroup);
      this.$sla.sendEvent(EAnalyticsEvent.impression);
      this.initialLoadCompleted = true;
    }
  }

  async loadPlans(abGroup: string) {
    const planResponse = await SubscriptionApi.getPlans(abGroup);
    this.plans = planResponse.data.plans;
    if (planResponse.data.group) {
      this.userModule.setABGroup(planResponse.data.group);
    }
  }

  @Watch('abGroup')
  onAbGroupChanged(newValue: string) {
    if (newValue && this.initialLoadCompleted) {
      this.loadPlans(newValue);
    }
  }

  get abGroup(): string {
    return this.userModule.abGroup;
  }

  get isFreeTrial(): boolean {
    return !this.userSubscription;
  }

  get sevenDaysFromNow(): string {
    return UtilHelper.dateToHumanReadable(moment().add(7, 'days'));
  }

  get checkoutButtonText(): string {
    if (this.isFreeTrial) return 'Start free trial';

    return 'Pay now';
  }

  get currentPlanName(): string {
    if (!this.selectedPlan) return '';
    return capitalize(this.selectedPlan);
  }

  get monthlyPlanName(): string {
    return ESubscriptionPlanType.monthly;
  }

  get annualPlanName(): string {
    return ESubscriptionPlanType.annual;
  }

  get currentPlanPrice(): string | number {
    if (!this.plans || !this.selectedPlan) return 0;

    return (this.plans[this.selectedPlan].price / 100).toFixed(2);
  }

  get currentPlanPeriod(): string {
    if (!this.selectedPlan) return '';
    return this.selectedPlan === ESubscriptionPlanType.monthly ? 'month' : 'year';
  }

  get firstBillingDate(): Date {
    const today = new Date();
    if (!this.isFreeTrial) return today;
    today.setDate(today.getDate() + 7);

    return today;
  }

  get nextBillingDate() {
    if (!this.firstBillingDate) return null;

    const firstBillingDate: Date = new Date(this.firstBillingDate);
    const dateToAdd: number = this.selectedPlan === ESubscriptionPlanType.monthly ? 30 : 365;

    firstBillingDate.setDate(firstBillingDate.getDate() + dateToAdd);

    return firstBillingDate;
  }

  get chargeDateText(): string {
    if (this.isFreeTrial) return `Your free trial ends on ${this.formatDate(this.firstBillingDate)} and you will be charged $${this.currentPlanPrice} on that date`;

    return `You will be charged $${this.currentPlanPrice} today`;
  }

  getPlanPrice(plan: TSubscriptionPlanType): string | number {
    if (!this.plans) return 0;

    return this.plans[plan].price / 100;
  }

  togglePlan() {
    if (this.selectedPlan === ESubscriptionPlanType.annual) {
      this.selectedPlan = ESubscriptionPlanType.monthly;
      return;
    }

    this.selectedPlan = ESubscriptionPlanType.annual;
  }

  async processOrder(braintreePayload) {
    if (!braintreePayload) {
      NotificationHelper.error('There was an error processing your order. Please try again.');
      this.$sla.sendEvent(this.isFreeTrial ? EAnalyticsEvent.checkoutErrorFreeTrial : EAnalyticsEvent.checkoutError,
        {
          message: 'Braintree payload missing',
          freeTrial: this.isFreeTrial,
          plan: this.selectedPlan,
        }
      );
      return;
    }

    this.paymentProcessing = true;
    const data = {
      planId: this.plans[this.selectedPlan].planId,
      paymentPayload: braintreePayload,
      hasAgreedToTerms: this.hasAgreedToTerms,
    };

    const response = await SubscriptionApi.subscribe(data);

    this.paymentProcessing = false;
    this.hasAgreedToTerms = false;

    if (!response.success) {
      NotificationHelper.error(response.message);
      this.$sla.sendEvent(this.isFreeTrial ? EAnalyticsEvent.checkoutErrorFreeTrial : EAnalyticsEvent.checkoutError,
        {
          message: response.message,
          freeTrial: this.isFreeTrial,
          plan: this.selectedPlan,
        }
      );
      return;
    }

    UtilHelper.trackGTMEvent({
      internal_user_id: `console_${this.currentUser.id}`,
      user_id: this.userIdentity?.id,
      event: EUserAdEvent.purchaseSubscription,
      event_category: EUserAdEvent.purchaseSubscription,
      value: response.subscription.plan_cost,
      transaction_id: response.subscription.id,
      currency: 'USD',
      coupon: '',
      subscription_source: 'console',
      sub_type: 'resurrection',
      subscription_length: response.subscription.plan_type,
      is_trial: !this.userSubscription,
      items: [
        {
          item_id:  response.subscription.plan_type === 'monthly' ? 'console-pro-monthly' : 'console-pro-annual',
          item_name: response.subscription.plan_type === 'monthly' ? 'Streamlabs Console Monthly' : 'Streamlabs Console Yearly',
        }
      ]
    });

    this.$sla.sendEvent(this.isFreeTrial ? EAnalyticsEvent.checkoutSuccessFreeTrial : EAnalyticsEvent.checkoutSuccess,
      {
        freeTrial: this.isFreeTrial,
        plan: this.selectedPlan,
      }
    );

    this.userSubscription = response.subscription;
    this.resetSelectedPlan();
    this.$sla.sendEvent(EAnalyticsEvent.paymentInfo, { type: braintreePayload.type });
  }

  selectPlan(plan: TSubscriptionPlanType) {
    this.selectedPlan = plan;
  }

  resetSelectedPlan() {
    this.selectedPlan = null;
  }

  skipFreeTrial(modalName: EModalName.checkoutMini = EModalName.checkoutMini) {
    if (this.fromModal) {
      this.$modal.hide(modalName);
      return;
    }

    if (this.$route.path !== '/projects') this.$router.push('/projects');
  }

  onCancelClick() {
    this.hasAgreedToTerms = false;

    this.$router.back();
  }

  onSubscribeButtonClick() {
    if (!this.hasAgreedToTerms) {
      this.displayAgreeToTermsError = true;
      return;
    }

    this.displayAgreeToTermsError = false;
    this.$refs.paymentForm.submit();
    this.$sla.sendEvent(this.isFreeTrial ? EAnalyticsEvent.checkoutAttemptFreeTrial : EAnalyticsEvent.checkoutAttempt,
      {
        freeTrial: this.isFreeTrial,
        plan: this.selectedPlan,
      }
    );
  }

  formatDate(date) {
    if (!date) return '';
    return date.toLocaleDateString('en-US');
  }

  onModalClosed() {
    this.resetSelectedPlan();
    this.hasAgreedToTerms = false;
    this.$sla.sendEvent(EAnalyticsEvent.checkoutModalClosed);
  }
}
